import React from 'react';
import { FilterOutlined, SearchOutlined, SortAscendingOutlined, StarFilled } from '@ant-design/icons';
import { Badge, Button, Dropdown, Input } from 'antd';

/**
 * DataTagFilterBar Molecule - Reusable filter bar component for data tags
 * 
 * @param {string} searchTerm - Current search term
 * @param {Function} setSearchTerm - Function to set search term
 * @param {Object} filterMenu - Ant Design Menu component for filters
 * @param {Function} isFilterActive - Function to check if filters are active
 * @param {Object} filters - Current filter values
 * @param {boolean} showPinnedOnly - Whether showing only pinned items
 * @param {number} filteredCount - Number of filtered items
 * @param {number} totalCount - Total number of items
 * @param {string} viewState - Current view state (card/list)
 * @param {Object} sortMenu - Ant Design Menu component for sorting
 * @param {Function} isSortActive - Function to check if sorting is active
 * @param {Array} pinnedTags - Array of pinned tag IDs
 * @param {Function} setShowPinnedOnly - Function to toggle show pinned only
 */
const DataTagFilterBar = ({
  searchTerm,
  setSearchTerm,
  filterMenu,
  isFilterActive,
  filters,
  showPinnedOnly,
  filteredCount,
  totalCount,
  viewState,
  sortMenu,
  isSortActive,
  pinnedTags,
  setShowPinnedOnly,
}) => {
  // Calculate active filter count
  const activeFilterCount = Object.values(filters).filter(Boolean).length + (showPinnedOnly ? 1 : 0);

  return (
    <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6 w-full md:w-auto">
      {/* Search Input */}
      <Input
        placeholder="Search tags"
        prefix={<SearchOutlined />}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full md:w-3/4"
      />
      
      {/* Filter Dropdown */}
      <Dropdown autoFocus menu={{ items: filterMenu }} trigger={['click']}>
        <Button 
          icon={<FilterOutlined />}
          className={isFilterActive() ? 'bg-blue-100 text-blue-600 border-blue-300 font-semibold' : 'font-medium'}
        >
          Filter {isFilterActive() && <Badge color='geekblue' count={activeFilterCount} />}
        </Button>
      </Dropdown>
      
      {/* Results Count Badge */}
      <Badge className="flex items-center justify-center bg-blue-50 border border-blue-200 rounded-full px-5 py-1 text-sm min-h-[2rem] min-w-[4rem] flex-shrink-0">
        <span className="text-blue-700 font-semibold">{filteredCount}</span>
        {filteredCount !== totalCount && (
          <span className="text-gray-500 ml-1 font-semibold">of {totalCount}</span>
        )}
      </Badge>
      
      {/* Sort Dropdown (only show in card view) */}
      {viewState === 'card' && (
        <Dropdown menu={{ items: sortMenu }} trigger={['click']}>
          <Button 
            icon={<SortAscendingOutlined />}
            className={isSortActive() ? 'bg-blue-100 text-blue-600 border-blue-300' : ''}
          >
            Sort 
          </Button>
        </Dropdown>
      )}
      
      {/* Favorites/Pinned Button */}
      {pinnedTags.length > 0 && (
        <Button
          icon={<StarFilled className='text-yellow-400' />}
          onClick={() => setShowPinnedOnly(!showPinnedOnly)}
          className={showPinnedOnly ? 'bg-blue-100 text-blue-600 border-blue-300' : ''}
        >
          Favorites {showPinnedOnly && <Badge color='geekblue' count={pinnedTags.length} />}
        </Button>
      )}
    </div>
  );
};

export default React.memo(DataTagFilterBar);
